import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { AIChat, AIFloatingButton } from '@/components/AIChat';
import { PersonalizedRecommendations, trackProductView } from '@/components/PersonalizedRecommendations';
import { 
  ShoppingBag, Building2, Car, TrendingUp, PiggyBank, 
  Landmark, ArrowRight, Search, ShoppingCart, Heart,
  Star, Zap, Shield, Globe, ChevronRight, X, Minus, Plus,
  MapPin, Bed, Bath, Gauge, Calendar, Store, Wallet,
  Users, Truck, Package, Video, Music, Ticket, Phone,
  BookOpen, Radio, Briefcase, DollarSign, Award
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_aimarketplace-18/artifacts/7akyhfla_IMG_5649.jpg';

const productCategories = [
  { id: 'all', name: 'All', icon: '🛒' },
  { id: 'electronics', name: 'Electronics', icon: '📱' },
  { id: 'fashion', name: 'Fashion', icon: '👟' },
  { id: 'home', name: 'Home', icon: '🏠' },
  { id: 'groceries', name: 'Groceries', icon: '🍎' },
  { id: 'beauty', name: 'Beauty', icon: '💄' },
  { id: 'sports', name: 'Sports', icon: '⚽' },
];

const marketCategories = [
  { id: 'all', name: 'All', icon: Store },
  { id: 'events', name: 'Events', icon: Calendar },
  { id: 'audiobooks', name: 'Audiobooks', icon: BookOpen },
  { id: 'streaming', name: 'Streaming', icon: Video },
  { id: 'telecom', name: 'Telecom', icon: Phone },
  { id: 'tickets', name: 'Tickets', icon: Ticket },
];

const verticals = [
  { icon: ShoppingBag, name: 'Jambo Buy', tab: 'products', color: 'from-cyan-500 to-teal-500' },
  { icon: Store, name: 'Jambo Markets', tab: 'markets', color: 'from-violet-500 to-purple-500' },
  { icon: Wallet, name: 'Jambo Wealth', tab: 'investments', color: 'from-green-500 to-emerald-500' },
  { icon: Briefcase, name: 'Jambo Capital', tab: 'capital', color: 'from-yellow-500 to-amber-500' },
  { icon: Building2, name: 'Jambo Homes', tab: 'properties', color: 'from-blue-500 to-indigo-500' },
  { icon: Car, name: 'Jambo Auto', tab: 'vehicles', color: 'from-orange-500 to-red-500' },
];

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency: 'KES',
    minimumFractionDigits: 0,
  }).format(amount);
};

export default function LandingPage() {
  const navigate = useNavigate();
  const { isAuthenticated, token } = useAuth();
  const [products, setProducts] = useState([]);
  const [properties, setProperties] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [digitalProducts, setDigitalProducts] = useState([]);
  const [capitalPrograms, setCapitalPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedMarketCategory, setSelectedMarketCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('products');
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [showAIChat, setShowAIChat] = useState(false);
  const [applicationForm, setApplicationForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    business_name: '',
    experience: '',
    reason: ''
  });

  useEffect(() => {
    // Load cart from localStorage
    const savedCart = localStorage.getItem('jambo_cart');
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [productsRes, propertiesRes, vehiclesRes, investmentsRes, digitalRes, capitalRes] = await Promise.all([
        axios.get(`${API_URL}/api/products?limit=50`),
        axios.get(`${API_URL}/api/properties?limit=20`),
        axios.get(`${API_URL}/api/vehicles?limit=20`),
        axios.get(`${API_URL}/api/investments?limit=20`),
        axios.get(`${API_URL}/api/digital-products?limit=30`),
        axios.get(`${API_URL}/api/capital-programs?limit=10`)
      ]);
      setProducts(productsRes.data.products || []);
      setProperties(propertiesRes.data.properties || []);
      setVehicles(vehiclesRes.data.vehicles || []);
      setInvestments(investmentsRes.data.investments || []);
      setDigitalProducts(digitalRes.data.digital_products || []);
      setCapitalPrograms(capitalRes.data.programs || []);
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const addToCart = (item, type = 'product') => {
    // Track the product interaction
    trackProductView(item.id, type, item.category, token);
    
    // For investments, use min_investment as price
    const itemPrice = type === 'investment' ? item.min_investment : item.price;
    const cartItem = { ...item, product_type: type, quantity: 1, price: itemPrice };
    const existingIndex = cart.findIndex(c => c.id === item.id);
    
    let newCart;
    if (existingIndex >= 0) {
      newCart = [...cart];
      newCart[existingIndex].quantity += 1;
    } else {
      newCart = [...cart, cartItem];
    }
    
    setCart(newCart);
    localStorage.setItem('jambo_cart', JSON.stringify(newCart));
    toast.success(`Added to cart!`);
    setShowCart(true);
  };

  const openApplicationModal = (program) => {
    setSelectedProgram(program);
    setShowApplicationModal(true);
  };

  const submitApplication = async () => {
    if (!applicationForm.full_name || !applicationForm.email || !applicationForm.phone || !applicationForm.reason) {
      toast.error('Please fill in all required fields');
      return;
    }
    try {
      const response = await axios.post(`${API_URL}/api/capital-programs/apply`, {
        program_id: selectedProgram.id,
        ...applicationForm
      });
      toast.success('Application approved! Welcome to Jambo Capital!');
      setShowApplicationModal(false);
      setApplicationForm({ full_name: '', email: '', phone: '', business_name: '', experience: '', reason: '' });
      // Navigate to partner dashboard if logged in
      if (isAuthenticated) {
        navigate('/partner');
      } else {
        toast.info('Login to access your Partner Dashboard');
      }
    } catch (error) {
      toast.error('Failed to submit application. Please try again.');
    }
  };

  const updateCartQuantity = (index, change) => {
    const newCart = [...cart];
    newCart[index].quantity = Math.max(1, newCart[index].quantity + change);
    setCart(newCart);
    localStorage.setItem('jambo_cart', JSON.stringify(newCart));
  };

  const removeFromCart = (index) => {
    const newCart = cart.filter((_, i) => i !== index);
    setCart(newCart);
    localStorage.setItem('jambo_cart', JSON.stringify(newCart));
    if (newCart.length === 0) setShowCart(false);
  };

  const cartTotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  const filteredProducts = products.filter(p => {
    if (selectedCategory !== 'all' && p.category !== selectedCategory) return false;
    if (searchQuery && !p.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const handleCheckout = () => {
    navigate('/checkout');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Sticky Header */}
      <header className="glass sticky top-0 z-50 border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Jambo" className="h-10 w-auto" />
            </Link>

            {/* Search - Desktop */}
            <div className="hidden md:flex flex-1 max-w-xl mx-8">
              <div className="relative w-full">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input 
                  placeholder="Search products, properties, vehicles..." 
                  className="pl-10 bg-muted/50 border-0"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="search-input"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                size="icon" 
                className="relative" 
                onClick={() => setShowCart(true)}
                data-testid="cart-btn"
              >
                <ShoppingCart className="h-5 w-5" />
                {cart.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-orange-500 text-white text-xs rounded-full flex items-center justify-center">
                    {cart.reduce((sum, item) => sum + item.quantity, 0)}
                  </span>
                )}
              </Button>
              
              {isAuthenticated ? (
                <Link to="/dashboard">
                  <Button className="rounded-full jambo-gradient text-white border-0" data-testid="dashboard-btn">
                    Dashboard
                  </Button>
                </Link>
              ) : (
                <Link to="/auth">
                  <Button className="rounded-full jambo-gradient text-white border-0" data-testid="join-btn">
                    Join Jambo
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-pattern py-6 lg:py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold tracking-tight">
                Shop, Invest & Own
                <span className="jambo-gradient-text"> — All in One</span>
              </h1>
              <p className="text-muted-foreground text-sm lg:text-base">
                Products • Properties • Vehicles • Investments — with financing options
              </p>
            </div>

            {/* Quick Stats */}
            <div className="flex items-center gap-6">
              <div className="text-center">
                <p className="text-xl font-bold jambo-gradient-text">50K+</p>
                <p className="text-xs text-muted-foreground">Products</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold jambo-gradient-text">500+</p>
                <p className="text-xs text-muted-foreground">Properties</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold jambo-gradient-text">15%</p>
                <p className="text-xs text-muted-foreground">Avg. Returns</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vertical Tabs & Quick Access */}
      <section className="border-b border-border sticky top-16 z-40 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="h-16 w-full justify-start bg-transparent gap-1 overflow-x-auto hide-scrollbar">
              {verticals.map((v) => (
                <TabsTrigger 
                  key={v.tab}
                  value={v.tab}
                  className={`flex items-center gap-2 px-5 py-3 rounded-full font-medium transition-all duration-200 ${
                    activeTab === v.tab 
                      ? 'bg-gradient-to-r from-cyan-500 via-green-500 to-orange-500 text-white shadow-lg scale-105' 
                      : 'bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                  data-testid={`tab-${v.tab}`}
                >
                  <v.icon className={`h-4 w-4 ${activeTab === v.tab ? 'text-white' : ''}`} />
                  <span className="whitespace-nowrap">{v.name}</span>
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      </section>

      {/* Main Content */}
      <main className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Personalized Recommendations */}
          {activeTab === 'products' && (
            <PersonalizedRecommendations onAddToCart={(product, type) => addToCart(product, type)} />
          )}

          {/* Products Tab */}
          {activeTab === 'products' && (
            <>
              {/* Category Pills */}
              <div className="mb-6">
                <div className="flex items-center gap-2 overflow-x-auto hide-scrollbar pb-2">
                  {productCategories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => setSelectedCategory(cat.id)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap text-sm font-medium transition-all ${
                        selectedCategory === cat.id
                          ? 'category-pill-active shadow-md'
                          : 'bg-muted hover:bg-muted/80'
                      }`}
                      data-testid={`category-${cat.id}`}
                    >
                      <span>{cat.icon}</span>
                      {cat.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Products Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {loading ? (
                  Array(10).fill(0).map((_, i) => (
                    <Card key={i} className="overflow-hidden">
                      <Skeleton className="aspect-square" />
                      <CardContent className="p-3">
                        <Skeleton className="h-4 w-3/4 mb-2" />
                        <Skeleton className="h-5 w-1/2" />
                      </CardContent>
                    </Card>
                  ))
                ) : (
                  filteredProducts.map((product, index) => (
                    <Card 
                      key={product.id}
                      className={`overflow-hidden product-card group animate-fade-in`}
                      data-testid={`product-${product.id}`}
                    >
                      <div className="aspect-square overflow-hidden bg-muted relative">
                        <img 
                          src={product.image_url || 'https://via.placeholder.com/300'}
                          alt={product.name}
                          className="product-image w-full h-full object-cover"
                        />
                        <Badge className="absolute top-2 left-2 bg-cyan-500 text-white text-xs">
                          {product.category}
                        </Badge>
                        {product.original_price && (
                          <Badge className="absolute top-2 right-2 bg-red-500 text-white text-xs">
                            15% OFF
                          </Badge>
                        )}
                      </div>
                      <CardContent className="p-3">
                        <p className="font-medium text-sm truncate mb-1">{product.name}</p>
                        <div className="flex items-center gap-1 mb-2">
                          <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                          <span className="text-xs text-muted-foreground">{product.rating} ({product.reviews})</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="price-tag text-cyan-600">{formatCurrency(product.price)}</p>
                            {product.original_price && (
                              <p className="text-xs text-muted-foreground line-through">{formatCurrency(product.original_price)}</p>
                            )}
                          </div>
                          <Button 
                            size="sm" 
                            className="h-8 rounded-full bg-gradient-to-r from-cyan-500 to-green-500 text-white"
                            onClick={() => addToCart(product, 'product')}
                            data-testid={`add-cart-${product.id}`}
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </>
          )}

          {/* Properties Tab */}
          {activeTab === 'properties' && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {loading ? (
                Array(6).fill(0).map((_, i) => (
                  <Card key={i} className="overflow-hidden">
                    <Skeleton className="h-48" />
                    <CardContent className="p-4"><Skeleton className="h-20" /></CardContent>
                  </Card>
                ))
              ) : (
                properties.map((property) => (
                  <Card key={property.id} className="overflow-hidden card-hover group" data-testid={`property-${property.id}`}>
                    <div className="h-48 overflow-hidden bg-muted relative">
                      <img src={property.image_url} alt={property.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                      <Badge className={`absolute top-3 left-3 ${property.property_type === 'sale' ? 'bg-green-500' : 'bg-orange-500'}`}>
                        For {property.property_type === 'sale' ? 'Sale' : 'Rent'}
                      </Badge>
                      {property.original_price && property.original_price > property.price && (
                        <Badge className="absolute top-3 right-3 bg-red-500 text-white text-xs">
                          {Math.round((1 - property.price / property.original_price) * 100)}% OFF
                        </Badge>
                      )}
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-semibold truncate mb-1">{property.title}</h3>
                      <p className="text-sm text-muted-foreground flex items-center gap-1 mb-2">
                        <MapPin className="h-3 w-3" /> {property.location}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground mb-3">
                        <span className="flex items-center gap-1"><Bed className="h-3 w-3" /> {property.bedrooms}</span>
                        <span className="flex items-center gap-1"><Bath className="h-3 w-3" /> {property.bathrooms}</span>
                        <span>{property.size_sqft?.toLocaleString()} sqft</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="price-tag text-cyan-600 text-lg">{formatCurrency(property.price)}{property.property_type === 'rent' && '/mo'}</p>
                          {property.original_price && property.original_price > property.price && (
                            <p className="text-xs text-muted-foreground line-through">{formatCurrency(property.original_price)}{property.property_type === 'rent' && '/mo'}</p>
                          )}
                        </div>
                        <Button size="sm" className="rounded-full jambo-gradient text-white" onClick={() => addToCart(property, 'property')}>
                          Enquire
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          )}

          {/* Vehicles Tab */}
          {activeTab === 'vehicles' && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {loading ? (
                Array(6).fill(0).map((_, i) => (
                  <Card key={i} className="overflow-hidden">
                    <Skeleton className="h-48" />
                    <CardContent className="p-4"><Skeleton className="h-20" /></CardContent>
                  </Card>
                ))
              ) : (
                vehicles.map((vehicle) => (
                  <Card key={vehicle.id} className="overflow-hidden card-hover group" data-testid={`vehicle-${vehicle.id}`}>
                    <div className="h-48 overflow-hidden bg-muted relative">
                      <img src={vehicle.image_url} alt={`${vehicle.make} ${vehicle.model}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                      <Badge className={`absolute top-3 left-3 ${vehicle.vehicle_type === 'buy' ? 'bg-cyan-500' : 'bg-purple-500'}`}>
                        {vehicle.vehicle_type === 'buy' ? 'For Sale' : 'Lease'}
                      </Badge>
                      <div className="absolute top-3 right-3 flex flex-col gap-1 items-end">
                        <Badge className={`${vehicle.condition === 'New' ? 'bg-green-500' : 'bg-amber-500'}`}>
                          {vehicle.condition}
                        </Badge>
                        {vehicle.original_price && vehicle.original_price > vehicle.price && (
                          <Badge className="bg-red-500 text-white text-xs">
                            {Math.round((1 - vehicle.price / vehicle.original_price) * 100)}% OFF
                          </Badge>
                        )}
                      </div>
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-semibold mb-1">{vehicle.make} {vehicle.model}</h3>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground mb-3">
                        <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {vehicle.year}</span>
                        <span className="flex items-center gap-1"><Gauge className="h-3 w-3" /> {vehicle.mileage?.toLocaleString()} km</span>
                        <span>{vehicle.fuel}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="price-tag text-cyan-600 text-lg">{formatCurrency(vehicle.price)}{vehicle.vehicle_type === 'lease' && '/mo'}</p>
                          {vehicle.original_price && vehicle.original_price > vehicle.price && (
                            <p className="text-xs text-muted-foreground line-through">{formatCurrency(vehicle.original_price)}{vehicle.vehicle_type === 'lease' && '/mo'}</p>
                          )}
                        </div>
                        <Button size="sm" className="rounded-full jambo-gradient text-white" onClick={() => addToCart(vehicle, 'vehicle')}>
                          <Plus className="h-4 w-4 mr-1" /> Cart
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          )}

          {/* Investments Tab (Jambo Wealth) */}
          {activeTab === 'investments' && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {loading ? (
                Array(6).fill(0).map((_, i) => (
                  <Card key={i}><CardContent className="p-5"><Skeleton className="h-32" /></CardContent></Card>
                ))
              ) : (
                investments.map((investment) => (
                  <Card key={investment.id} className="card-hover investment-gradient" data-testid={`investment-${investment.id}`}>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-green-500 flex items-center justify-center">
                          <TrendingUp className="h-6 w-6 text-white" />
                        </div>
                        <div className="text-right">
                          <Badge className="bg-green-500 text-white">+{investment.return_rate}%</Badge>
                          <p className="text-xs text-muted-foreground mt-1">{investment.risk_level} Risk</p>
                        </div>
                      </div>
                      <h3 className="font-semibold mb-1">{investment.name}</h3>
                      <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{investment.description}</p>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {investment.features?.slice(0, 2).map((f, i) => (
                          <Badge key={i} variant="outline" className="text-xs">{f}</Badge>
                        ))}
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-muted-foreground">Min. Investment</p>
                          <p className="font-bold">{formatCurrency(investment.min_investment)}</p>
                        </div>
                        <Button className="rounded-full jambo-gradient text-white" onClick={() => addToCart(investment, 'investment')}>
                          Invest
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          )}

          {/* Digital Products Tab (Jambo Markets) */}
          {activeTab === 'markets' && (
            <>
              {/* Market Category Pills */}
              <div className="mb-6">
                <div className="flex items-center gap-2 overflow-x-auto hide-scrollbar pb-2">
                  {marketCategories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => setSelectedMarketCategory(cat.id)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap text-sm font-medium transition-all ${
                        selectedMarketCategory === cat.id
                          ? 'jambo-gradient text-white shadow-md'
                          : 'bg-muted hover:bg-muted/80'
                      }`}
                      data-testid={`market-category-${cat.id}`}
                    >
                      <cat.icon className="h-4 w-4" />
                      {cat.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Digital Products Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {loading ? (
                  Array(8).fill(0).map((_, i) => (
                    <Card key={i} className="overflow-hidden">
                      <Skeleton className="aspect-video" />
                      <CardContent className="p-4"><Skeleton className="h-24" /></CardContent>
                    </Card>
                  ))
                ) : (
                  digitalProducts
                    .filter(dp => selectedMarketCategory === 'all' || dp.category === selectedMarketCategory)
                    .map((product) => (
                      <Card key={product.id} className="overflow-hidden card-hover group" data-testid={`digital-${product.id}`}>
                        <div className="aspect-video overflow-hidden bg-muted relative">
                          <img src={product.image_url} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                          <Badge className={`absolute top-3 left-3 ${
                            product.category === 'events' ? 'bg-purple-500' :
                            product.category === 'audiobooks' ? 'bg-blue-500' :
                            product.category === 'streaming' ? 'bg-red-500' :
                            product.category === 'telecom' ? 'bg-green-500' :
                            'bg-amber-500'
                          }`}>
                            {product.category.charAt(0).toUpperCase() + product.category.slice(1)}
                          </Badge>
                          {product.original_price && product.original_price > product.price && (
                            <Badge className="absolute top-3 right-3 bg-red-500 text-white text-xs">
                              {Math.round((1 - product.price / product.original_price) * 100)}% OFF
                            </Badge>
                          )}
                        </div>
                        <CardContent className="p-4">
                          <h3 className="font-semibold mb-1 line-clamp-1">{product.name}</h3>
                          <p className="text-sm text-muted-foreground mb-2 line-clamp-2">{product.description}</p>
                          
                          {/* Category-specific details */}
                          <div className="flex flex-wrap gap-2 text-xs text-muted-foreground mb-3">
                            {product.date && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {product.date}</span>}
                            {product.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {product.location}</span>}
                            {product.duration && <span className="flex items-center gap-1"><Radio className="h-3 w-3" /> {product.duration}</span>}
                            {product.billing && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {product.billing}</span>}
                            {product.validity && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {product.validity}</span>}
                          </div>
                          
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="price-tag text-cyan-600 text-lg">{formatCurrency(product.price)}</p>
                              {product.original_price && product.original_price > product.price && (
                                <p className="text-xs text-muted-foreground line-through">{formatCurrency(product.original_price)}</p>
                              )}
                            </div>
                            <Button size="sm" className="rounded-full jambo-gradient text-white" onClick={() => addToCart(product, 'digital')}>
                              <Plus className="h-4 w-4 mr-1" /> Buy
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                )}
              </div>
            </>
          )}

          {/* Capital Programs Tab (Jambo Capital) */}
          {activeTab === 'capital' && (
            <>
              <div className="text-center mb-8">
                <h2 className="text-2xl md:text-3xl font-bold mb-2">Earn with <span className="jambo-gradient-text">Jambo Capital</span></h2>
                <p className="text-muted-foreground max-w-2xl mx-auto">Join our network of partners and turn your skills, resources, or audience into income. Multiple earning opportunities available.</p>
              </div>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {loading ? (
                  Array(6).fill(0).map((_, i) => (
                    <Card key={i}><CardContent className="p-6"><Skeleton className="h-48" /></CardContent></Card>
                  ))
                ) : (
                  capitalPrograms.map((program) => (
                    <Card key={program.id} className="card-hover overflow-hidden" data-testid={`program-${program.id}`}>
                      <div className="h-40 overflow-hidden bg-muted relative">
                        <img src={program.image_url} alt={program.name} className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                        <div className="absolute bottom-3 left-3 right-3">
                          <Badge className="bg-amber-500 text-white mb-1">{program.commission_rate}</Badge>
                          <h3 className="font-bold text-white text-lg">{program.name}</h3>
                        </div>
                      </div>
                      <CardContent className="p-4">
                        <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{program.description}</p>
                        
                        <div className="bg-green-50 dark:bg-green-950/30 rounded-lg p-3 mb-4">
                          <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                            <DollarSign className="h-4 w-4" />
                            <span className="text-sm font-medium">Earning Potential</span>
                          </div>
                          <p className="font-bold text-green-700 dark:text-green-300">{program.earning_potential}</p>
                        </div>
                        
                        <div className="mb-4">
                          <p className="text-xs font-medium mb-2 text-muted-foreground">Requirements:</p>
                          <div className="flex flex-wrap gap-1">
                            {program.requirements?.slice(0, 2).map((req, i) => (
                              <Badge key={i} variant="outline" className="text-xs">{req}</Badge>
                            ))}
                            {program.requirements?.length > 2 && (
                              <Badge variant="outline" className="text-xs">+{program.requirements.length - 2} more</Badge>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex gap-2">
                          <Button 
                            className="flex-1 rounded-full jambo-gradient text-white"
                            onClick={() => openApplicationModal(program)}
                          >
                            Apply Now
                          </Button>
                          <Button variant="outline" className="rounded-full" onClick={() => toast.info('Program details coming soon!')}>
                            Learn More
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </main>

      {/* Cart Drawer */}
      {showCart && (
        <div className="fixed inset-0 z-50" onClick={() => setShowCart(false)}>
          <div className="absolute inset-0 bg-black/50" />
          <div 
            className="absolute right-0 top-0 h-full w-full max-w-md bg-background shadow-xl animate-slide-up"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-between p-4 border-b">
                <h2 className="text-lg font-bold">Your Cart ({cart.length})</h2>
                <button onClick={() => setShowCart(false)} className="p-2 hover:bg-muted rounded-full">
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {cart.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <ShoppingCart className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Your cart is empty</p>
                  </div>
                ) : (
                  cart.map((item, index) => (
                    <div key={index} className="flex gap-3 p-3 rounded-xl bg-muted/50">
                      <img 
                        src={item.image_url || 'https://via.placeholder.com/80'} 
                        alt={item.name || item.title || item.make}
                        className="w-20 h-20 object-cover rounded-lg"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{item.name || item.title || `${item.make} ${item.model}`}</p>
                        <p className="text-xs text-muted-foreground capitalize">{item.product_type}</p>
                        <p className="font-bold text-cyan-600 mt-1">{formatCurrency(item.price)}</p>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <button onClick={() => removeFromCart(index)} className="text-red-500 hover:bg-red-50 p-1 rounded">
                          <X className="h-4 w-4" />
                        </button>
                        <div className="flex items-center gap-1">
                          <button onClick={() => updateCartQuantity(index, -1)} className="w-6 h-6 rounded bg-muted flex items-center justify-center">
                            <Minus className="h-3 w-3" />
                          </button>
                          <span className="w-6 text-center text-sm">{item.quantity}</span>
                          <button onClick={() => updateCartQuantity(index, 1)} className="w-6 h-6 rounded bg-muted flex items-center justify-center">
                            <Plus className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {cart.length > 0 && (
                <div className="p-4 border-t space-y-4">
                  <div className="flex justify-between text-lg font-bold">
                    <span>Total</span>
                    <span className="jambo-gradient-text">{formatCurrency(cartTotal)}</span>
                  </div>
                  <p className="text-xs text-muted-foreground text-center">
                    Financing available at checkout • Free delivery over KES 5,000
                  </p>
                  <Button 
                    onClick={handleCheckout}
                    className="w-full h-12 rounded-full jambo-gradient text-white text-lg"
                    data-testid="checkout-btn"
                  >
                    Checkout
                    <ArrowRight className="h-5 w-5 ml-2" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Capital Application Modal */}
      <Dialog open={showApplicationModal} onOpenChange={setShowApplicationModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Apply for {selectedProgram?.name}</DialogTitle>
            <DialogDescription>
              Fill out the form below and our team will review your application within 48 hours.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-amber-50 dark:bg-amber-950/30 rounded-lg p-3 mb-4">
              <p className="text-sm text-amber-700 dark:text-amber-300">
                <strong>Earning Potential:</strong> {selectedProgram?.earning_potential}
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="full_name">Full Name *</Label>
                <Input 
                  id="full_name"
                  value={applicationForm.full_name}
                  onChange={(e) => setApplicationForm({...applicationForm, full_name: e.target.value})}
                  placeholder="John Doe"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number *</Label>
                <Input 
                  id="phone"
                  value={applicationForm.phone}
                  onChange={(e) => setApplicationForm({...applicationForm, phone: e.target.value})}
                  placeholder="+254 712 345 678"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">Email Address *</Label>
              <Input 
                id="email"
                type="email"
                value={applicationForm.email}
                onChange={(e) => setApplicationForm({...applicationForm, email: e.target.value})}
                placeholder="john@example.com"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="business_name">Business Name (Optional)</Label>
              <Input 
                id="business_name"
                value={applicationForm.business_name}
                onChange={(e) => setApplicationForm({...applicationForm, business_name: e.target.value})}
                placeholder="Your business name"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="experience">Relevant Experience (Optional)</Label>
              <Input 
                id="experience"
                value={applicationForm.experience}
                onChange={(e) => setApplicationForm({...applicationForm, experience: e.target.value})}
                placeholder="e.g., 2 years in sales"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="reason">Why do you want to join this program? *</Label>
              <Textarea 
                id="reason"
                value={applicationForm.reason}
                onChange={(e) => setApplicationForm({...applicationForm, reason: e.target.value})}
                placeholder="Tell us why you're interested..."
                rows={3}
              />
            </div>
          </div>
          
          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={() => setShowApplicationModal(false)}>Cancel</Button>
            <Button className="jambo-gradient text-white" onClick={submitApplication}>
              Submit Application
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Trust Footer */}
      <footer className="bg-muted/30 border-t border-border py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-3 gap-6 text-center mb-8">
            <div className="flex flex-col items-center gap-2">
              <Shield className="h-8 w-8 text-cyan-500" />
              <p className="font-medium text-sm">Secure Payments</p>
            </div>
            <div className="flex flex-col items-center gap-2">
              <Zap className="h-8 w-8 text-green-500" />
              <p className="font-medium text-sm">Instant Financing</p>
            </div>
            <div className="flex flex-col items-center gap-2">
              <Globe className="h-8 w-8 text-orange-500" />
              <p className="font-medium text-sm">Kenya & GCC</p>
            </div>
          </div>
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <img src={LOGO_URL} alt="Jambo" className="h-8" />
            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <a href="#" className="hover:text-foreground">Privacy</a>
              <a href="#" className="hover:text-foreground">Terms</a>
              <a href="#" className="hover:text-foreground">Contact</a>
            </div>
            <p className="text-sm text-muted-foreground">© 2024 myjambo.co</p>
          </div>
        </div>
      </footer>

      {/* AI Chat */}
      <AIFloatingButton onClick={() => setShowAIChat(true)} />
      <AIChat isOpen={showAIChat} onClose={() => setShowAIChat(false)} />
    </div>
  );
}
